import React from "react";

import { Controller, useFormContext, useWatch } from "react-hook-form";

import { AgentVersionConfigurationInput } from "src/agents/types";
import {
  AgentModelInfo,
  Model,
  ServiceTier,
} from "src/clients/agent-management";
import { ModelOption, ModelPicker } from "src/genai/ModelSelector";
import {
  hostingForModel,
  lifecycleFor,
  regionFromRouting,
  rerouteTargetIdFor,
  successorIdFor,
} from "src/genai/ModelSelector/facets";
import {
  withByokPricing,
  withTpmQuota,
} from "src/genai/ModelSelector/modelOptions";

type AgentModelSelectorProps = {
  /** Whether the selector is disabled (e.g., for published versions) */
  disabled?: boolean;
  /** Available models adapted for the picker */
  modelOptions: ModelOption[];
  /** The same models unadapted, for capabilities the picker does not carry */
  rawModels: AgentModelInfo[];
};

/**
 * Build the compound model option ID used to highlight the selected model.
 * BYOK entries use "model_id:byok:credential_id", Taktile defaults use plain model_id.
 */
const buildSelectedOptionId = (
  model: string,
  credentialId: string | null | undefined,
): string => {
  if (credentialId) {
    return `${model}:byok:${credentialId}`;
  }
  return model;
};

/**
 * Parse a compound model option ID back into model and credential_id.
 * "gpt-5:byok:uuid" → { model: "gpt-5", credentialId: "uuid" }
 * "gpt-5" → { model: "gpt-5", credentialId: null }
 */
const parseSelectedOptionId = (
  optionId: string,
): { model: string; credentialId: string | null } => {
  const byokMarker = ":byok:";
  const idx = optionId.indexOf(byokMarker);
  if (idx !== -1) {
    return {
      model: optionId.slice(0, idx),
      credentialId: optionId.slice(idx + byokMarker.length),
    };
  }
  return { model: optionId, credentialId: null };
};

/**
 * Model selector component for agents.
 * Models are passed down from the parent component which fetches them.
 */
export const AgentModelSelector: React.FC<AgentModelSelectorProps> = ({
  disabled = false,
  modelOptions,
  rawModels,
}) => {
  const { control, setValue } =
    useFormContext<AgentVersionConfigurationInput>();
  // Subscribe to credential_id changes so the component re-renders when
  // switching between shared-key and BYOK variants of the same model.
  // Using getValues() would only read the value once and miss updates
  // when the model name stays the same but the credential changes.
  const credentialId = useWatch({
    control,
    name: "model_config.credential_id",
  });
  const serviceTier = useWatch({ control, name: "model_config.service_tier" });

  return (
    <Controller
      control={control}
      name="model_config.model"
      render={({ field }) => {
        const selectedId = buildSelectedOptionId(field.value, credentialId);
        // A non-null credential_id with no matching option means the BYOK
        // credential was deleted — the raw compound id is useless, so surface a
        // friendly label that still names the underlying model when we can
        // resolve a display name (from a shared or sibling BYOK option).
        const isDeletedBYOKCredential =
          credentialId != null &&
          !modelOptions.some((m) => m.id === selectedId);
        const modelDisplayName =
          modelOptions.find(
            (m) =>
              m.id === field.value || m.id.startsWith(`${field.value}:byok:`),
          )?.displayName ?? field.value;

        return (
          <ModelPicker
            dataLoc="agent-model-selector"
            isReadOnly={disabled}
            missingModelDisplayName={
              isDeletedBYOKCredential
                ? `${modelDisplayName} using a deleted custom AI credential`
                : undefined
            }
            models={modelOptions}
            selectedModelId={selectedId}
            onSelect={(optionId) => {
              const parsed = parseSelectedOptionId(optionId);
              field.onChange(parsed.model as Model);
              setValue("model_config.credential_id", parsed.credentialId, {
                shouldDirty: true,
              });
              // Fast mode is a per-model capability. Drop a stale tier when the
              // newly selected model cannot be served on it, so the version
              // never asks for a tier its provider would reject — and never
              // leaves one stored behind a hidden toggle. Only touch the field
              // when a tier is actually set: a version that never opted in
              // keeps sending exactly the config it sent before fast mode.
              const supportsFastMode = rawModels.some(
                (model) =>
                  model.model_id === parsed.model &&
                  model.supported_service_tiers?.includes(ServiceTier.FAST),
              );
              if (serviceTier && !supportsFastMode) {
                setValue("model_config.service_tier", null, {
                  shouldDirty: true,
                });
              }
            }}
          />
        );
      }}
    />
  );
};

export const convertAgentModelInfoToModelOption = (
  model: AgentModelInfo,
): ModelOption => ({
  id: buildSelectedOptionId(model.model_id, model.credential_id),
  displayName: model.display_name,
  provider: model.provider,
  lifecycle: lifecycleFor(model),
  sunsetDate: model.sunset_date ?? undefined,
  hosting: hostingForModel(model),
  region: regionFromRouting(model.routing_target_region),
  byok: Boolean(model.credential_id),
  deploymentLabel: model.credential_id
    ? (model.credential_name ?? undefined)
    : undefined,
  successorId: successorIdFor(model),
  rerouteTargetId: rerouteTargetIdFor(model),
  intelligence: model.intelligence,
  speed: model.speed,
  inputCostPer1MTokens: model.input_cost_per_1m_tokens ?? null,
  outputCostPer1MTokens: model.output_cost_per_1m_tokens ?? null,
  contextWindowTokens: model.context_window_size,
  releaseDate: model.release_date,
  // Merged from the workspace LLM quota by withTpmQuota in agentModelsToOptions.
  tokensPerMinuteQuota: null,
});

/**
 * Adapt the agent model list into {@link ModelOption}s: backfill BYOK pricing
 * from the shared sibling and merge the per-model TPM quota. Agents share the
 * workspace LLM quota with aiNode (`tpmByModelId` keyed by `model_id`).
 */
export const agentModelsToOptions = (
  models: AgentModelInfo[],
  tpmByModelId: Record<string, number> = {},
): ModelOption[] =>
  withByokPricing(
    withTpmQuota(models.map(convertAgentModelInfoToModelOption), tpmByModelId),
  );
